import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle, Loader2, FileText, DollarSign, Bell, 
  FolderOpen, TrendingUp, Clock, Zap, Play
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export default function AutoBooksLibraryPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeAutomations, setActiveAutomations] = useState([]);
  const [creatingType, setCreatingType] = useState(null);

  const automationTemplates = [
    {
      type: "invoice_processing",
      name: "Invoice Processing",
      description: "Automatically extract invoice data from PDF emails and create QuickBooks entries",
      icon: FileText,
      color: "bg-blue-500",
      confidence: 0.95,
      hoursSaved: 10,
      complexity: "Easy",
      status: "active_by_default",
      requirements: ["QuickBooks", "Gmail"]
    },
    {
      type: "expense_tracking",
      name: "Expense Tracking",
      description: "Auto-categorize and track expenses from receipt emails to QuickBooks",
      icon: DollarSign,
      color: "bg-green-500",
      confidence: 0.88,
      hoursSaved: 5,
      complexity: "Easy",
      status: "active_by_default",
      requirements: ["QuickBooks", "Gmail"]
    },
    {
      type: "client_notification",
      name: "Client Notification",
      description: "Send payment request emails automatically when invoices are created in QuickBooks",
      icon: Bell,
      color: "bg-purple-500",
      confidence: 0.75,
      hoursSaved: 3,
      complexity: "Medium",
      status: "optional",
      requirements: ["QuickBooks"]
    },
    {
      type: "tax_organization",
      name: "Tax Document Organization",
      description: "Automatically file tax documents to Google Drive organized by tax year",
      icon: FolderOpen,
      color: "bg-amber-500",
      confidence: 0.82,
      hoursSaved: 4,
      complexity: "Medium",
      status: "optional",
      requirements: ["Gmail", "Google Drive"]
    },
    {
      type: "bank_reconciliation",
      name: "Bank Reconciliation",
      description: "Daily automatic comparison of QuickBooks balance vs bank balance with alerts",
      icon: TrendingUp,
      color: "bg-red-500",
      confidence: 0.70,
      hoursSaved: 6,
      complexity: "Advanced",
      status: "advanced",
      requirements: ["QuickBooks", "Bank API"]
    }
  ];

  useEffect(() => {
    fetchActiveAutomations();
  }, []);

  const fetchActiveAutomations = async () => {
    try {
      const response = await axios.get(`${API}/workflows`, { withCredentials: true });
      const autobooks = response.data.filter(w => w.category?.startsWith('autobooks_'));
      setActiveAutomations(autobooks.map(w => w.category));
    } catch (error) {
      console.error('Error fetching automations:', error);
    }
  };

  const handleCreateAutomation = async (type) => {
    setCreatingType(type);
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/onboarding/autobooks/automation/create-template?automation_type=${type}`,
        {},
        { withCredentials: true }
      );
      
      toast.success(`${response.data.message}`);
      fetchActiveAutomations();
      
    } catch (error) {
      console.error('Error creating automation:', error);
      toast.error(error.response?.data?.detail || "Failed to create automation");
    } finally {
      setLoading(false);
      setCreatingType(null);
    }
  };

  const isAutomationActive = (type) => {
    return activeAutomations.includes(`autobooks_${type.split('_')[0]}`);
  };

  const getComplexityColor = (complexity) => {
    switch(complexity) {
      case 'Easy': return 'text-green-500';
      case 'Medium': return 'text-amber-500';
      case 'Advanced': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-semibold text-foreground">AutoBooks Automation Library</h1>
          <p className="text-muted-foreground mt-2">
            Pre-built automation templates designed specifically for accountants and bookkeepers
          </p>
        </div>

        {/* Stats Card */}
        <Card className="glass-card border-primary/30">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-heading font-semibold text-primary mb-1">
                  {automationTemplates.reduce((sum, t) => sum + t.hoursSaved, 0)}h/week
                </div>
                <p className="text-sm text-muted-foreground">Total Time Savings Potential</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-heading font-semibold text-primary mb-1">
                  {activeAutomations.length}/{automationTemplates.length}
                </div>
                <p className="text-sm text-muted-foreground">Active Automations</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-heading font-semibold text-primary mb-1">
                  ${automationTemplates.reduce((sum, t) => sum + t.hoursSaved * 50, 0)}/week
                </div>
                <p className="text-sm text-muted-foreground">Estimated Value</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Automation Templates Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {automationTemplates.map((template) => {
            const Icon = template.icon;
            const isActive = isAutomationActive(template.type);
            
            return (
              <Card 
                key={template.type} 
                className={`glass-card hover:border-primary/50 transition-all ${
                  isActive ? 'border-green-500/50 bg-green-500/5' : ''
                }`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl ${template.color} flex items-center justify-center`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="font-heading text-lg">{template.name}</CardTitle>
                        {isActive && (
                          <Badge variant="outline" className="mt-1 text-xs bg-green-500/10 text-green-500 border-green-500/30">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${getComplexityColor(template.complexity)}`}
                    >
                      {template.complexity}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <CardDescription>{template.description}</CardDescription>
                  
                  {/* Metrics */}
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>{template.hoursSaved}h/week saved</span>
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Zap className="w-4 h-4" />
                      <span>{Math.round(template.confidence * 100)}% confidence</span>
                    </div>
                  </div>

                  {/* Confidence Bar */}
                  <div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span>AI Confidence</span>
                      <span>{Math.round(template.confidence * 100)}%</span>
                    </div>
                    <Progress value={template.confidence * 100} className="h-2" />
                  </div>

                  {/* Requirements */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Required integrations:</p>
                    <div className="flex flex-wrap gap-2">
                      {template.requirements.map((req, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {req}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Action Button */}
                  <Button
                    onClick={() => handleCreateAutomation(template.type)}
                    disabled={isActive || loading}
                    className="w-full rounded-full"
                    variant={isActive ? "outline" : "default"}
                  >
                    {loading && creatingType === template.type ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : isActive ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Active
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Activate Automation
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Help Card */}
        <Card className="glass-card border-primary/30">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground mb-2">Need Help Setting Up?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Each automation requires specific integrations to be connected. Make sure you've set up the required integrations 
                  before activating an automation. Visit the Integrations page to connect your accounts.
                </p>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/integrations')}
                  className="rounded-full"
                >
                  Manage Integrations
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
